import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/shared/api/useApi';
import { Button } from '@/shared/components';
import { PluginProblem } from './PluginProblem';

const labels: Record<string, string> = {
  Syncing: 'Daten werden abgeglichen',
  Ready: 'Abgleich bereit',
  Degraded: 'Abgleich unterbrochen',
  ResyncRequired: 'Neuer vollständiger Abgleich erforderlich',
  Pending: 'Verbindung ausstehend',
  Disabled: 'Abgleich gesperrt',
  Revoked: 'Löschung angefordert',
  AccessDenied: 'Nutzungsrecht fehlt',
};

export function PluginSyncStatus({
  installationId,
  installationState = 'Active',
  grantRevision = 0,
}: {
  installationId: string;
  installationState?: string;
  grantRevision?: number;
}) {
  const api = useApi();
  const client = useQueryClient();
  const queryKey = ['plugin-sync', installationId, grantRevision];
  const health = useQuery({
    queryKey,
    queryFn: () => api.plugins.syncHealth(installationId),
    refetchInterval: 15000,
    refetchOnWindowFocus: 'always',
    retry: false,
  });
  const retry = useMutation({
    mutationFn: () => api.plugins.retrySync(installationId),
    onSettled: () => client.invalidateQueries({ queryKey }),
  });
  return (
    <section className="border-line space-y-3 rounded-lg border p-4" aria-label="Datenabgleich">
      <h2 className="font-serif text-xl">Datenabgleich</h2>
      {health.isError ? (
        <p role="alert">Der aktuelle Abgleichstatus ist nicht erreichbar.</p>
      ) : health.isPending ? (
        <p role="status">Status wird geladen …</p>
      ) : (
        <>
          <p role="status" className="font-medium">
            {labels[installationState === 'Active' ? health.data.state : installationState] ??
              'Status wird geprüft'}
          </p>
          {health.data.pending > 0 && (
            <p className="text-ink-soft text-sm">
              {health.data.pending} Änderungen warten auf Bestätigung.
            </p>
          )}
          {health.data.lastSyncedAt && (
            <p className="text-ink-soft text-sm">
              Zuletzt abgeglichen: {new Date(health.data.lastSyncedAt).toLocaleString('de-DE')}
            </p>
          )}
          {health.data.cleanupPending && (
            <p className="text-ink-soft text-sm">
              Die Sperrung oder Löschung wird noch vom verbundenen Produkt bestätigt.
            </p>
          )}
          {health.data.deadLetters > 0 && (
            <Button
              type="button"
              size="lg"
              disabled={retry.isPending}
              onClick={() => retry.mutate()}
            >
              {retry.isPending ? 'Wird angefordert …' : 'Zustellungen erneut versuchen'}
            </Button>
          )}
        </>
      )}
      <PluginProblem error={retry.error} />
      <p className="text-ink-soft text-sm">
        Der Abgleich umfasst die in StayOn vorhandenen Daten. Er bestätigt keine vollständige
        Lieferung des Buchungsportals.
      </p>
    </section>
  );
}
